import React, { useState } from 'react';
import { X, Eye, EyeOff, Send } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth, db } from '../../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { analytics } from '../../services/analytics';
import toast from 'react-hot-toast';

const VILLES_CI = [
  'Abidjan', 'Bouaké', 'Daloa', 'Yamoussoukro', 'San Pedro',
  'Korhogo', 'Man', 'Divo', 'Gagnoa', 'Abengourou',
  'Soubré', 'Sassandra', 'Odienné', 'Bondoukou', 'Touba',
];

const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RE_PHONE = /^\+225\s?\d{2}\s?\d{2}\s?\d{2}\s?\d{2}\s?\d{2}$/;

const defaultForm = {
  email: '', password: '', confirmPassword: '',
  prenom: '', nom: '', telephone: '', contactUrgence: '', ville: 'Abidjan',
  nombreResidences: '',
};

export default function AuthModal({ role, onClose, onSuccess }) {
  const [mode, setMode] = useState('login');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const { login, signup } = useAuth();
  const navigate = useNavigate();

  const isGerant = role === 'gerant';
  const titre = isGerant ? 'Espace Gérant' : 'Connexion / Inscription';

  function change(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function afterAuthNavigate(uid) {
    const snap = await getDoc(doc(db, 'users', uid));
    const profile = snap.data();
    onClose();
    if (profile?.role === 'admin') { navigate('/admin/dashboard'); return; }
    if (profile?.role === 'gerant') { navigate('/gerant/dashboard'); return; }
    // Client : on reste sur place pour ne pas perdre la sélection en cours
    // (ex : reprendre la réservation démarrée avant la connexion).
    onSuccess?.();
  }

  async function handleResetPassword() {
    if (!form.email) { toast.error('Saisis ton email d\'abord'); return; }
    if (!RE_EMAIL.test(form.email)) { toast.error('Email invalide'); return; }
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, form.email);
      toast.success('Email de réinitialisation envoyé !');
    } catch {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setResetLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!RE_EMAIL.test(form.email)) { toast.error('Email invalide'); return; }

    if (mode === 'signup') {
      if (!form.prenom || !form.nom || !form.telephone) { toast.error('Veuillez remplir tous les champs obligatoires'); return; }
      if (!RE_PHONE.test(form.telephone)) { toast.error('Format contact principal : +225 07 XX XX XX XX'); return; }
      if (!isGerant && !RE_PHONE.test(form.contactUrgence)) { toast.error('Format contact d\'urgence : +225 07 XX XX XX XX'); return; }
      if (form.password.length < 8) { toast.error('Mot de passe : 8 caractères minimum'); return; }
      if (form.password !== form.confirmPassword) { toast.error('Les mots de passe ne correspondent pas'); return; }
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        const { user } = await login(form.email, form.password);
        toast.success('Connexion réussie !');
        await afterAuthNavigate(user.uid);
      } else {
        const profileData = isGerant
          ? {
              nom: form.nom, prenom: form.prenom, telephone: form.telephone,
              ville: form.ville, role: 'gerant',
              nombreResidences: Number(form.nombreResidences) || 0,
              statutValidation: 'en attente',
              photoURL: '',
            }
          : {
              nom: form.nom, prenom: form.prenom, telephone: form.telephone,
              contactUrgence: form.contactUrgence, ville: form.ville, role: 'client',
              photoURL: '',
            };
        const user = await signup(form.email, form.password, profileData);
        if (isGerant) {
          analytics.inscriptionGerant(form.ville);
          toast.success('Compte créé ! Il sera activé après vérification par notre équipe.', { duration: 6000 });
        } else {
          analytics.inscriptionClient(form.ville);
          toast.success('Compte créé avec succès !');
        }
        await afterAuthNavigate(user.uid);
      }
    } catch (err) {
      const msgs = {
        'auth/user-not-found': 'Aucun compte avec cet email',
        'auth/wrong-password': 'Mot de passe incorrect',
        'auth/email-already-in-use': 'Email déjà utilisé',
        'auth/weak-password': 'Mot de passe trop faible (8 caractères min)',
        'auth/invalid-email': 'Email invalide',
        'auth/invalid-credential': 'Email ou mot de passe incorrect',
      };
      toast.error(msgs[err.code] || 'Erreur : ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 460, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h3 style={{ margin: 0 }}>{titre}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="tabs" style={{ marginBottom: '1.5rem' }}>
          <button type="button" className={`tab-btn ${mode === 'login' ? 'active' : ''}`} onClick={() => setMode('login')} style={{ flex: 1 }}>
            Connexion{isGerant ? ' Gérant' : ''}
          </button>
          <button type="button" className={`tab-btn ${mode === 'signup' ? 'active' : ''}`} onClick={() => setMode('signup')} style={{ flex: 1 }}>
            Inscription{isGerant ? ' Gérant' : ''}
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {mode === 'signup' && (
            <>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Prénom *</label>
                  <input name="prenom" value={form.prenom} onChange={change} placeholder="Jean" className="form-control" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Nom *</label>
                  <input name="nom" value={form.nom} onChange={change} placeholder="Kouassi" className="form-control" required />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Contact principal *</label>
                <input name="telephone" value={form.telephone} onChange={change} placeholder="+225 07 12 34 56 78" className="form-control" required />
              </div>

              {!isGerant && (
                <div className="form-group">
                  <label className="form-label">Contact d'urgence *</label>
                  <input name="contactUrgence" value={form.contactUrgence} onChange={change} placeholder="+225 05 98 76 54 32" className="form-control" required />
                </div>
              )}

              <div className="form-group">
                <label className="form-label">{isGerant ? 'Ville de résidence *' : 'Ville'}</label>
                <select name="ville" value={form.ville} onChange={change} className="form-control">
                  {VILLES_CI.map(v => <option key={v}>{v}</option>)}
                </select>
              </div>

              {isGerant && (
                <div className="form-group">
                  <label className="form-label">Nombre de résidences (optionnel)</label>
                  <input type="number" min={0} name="nombreResidences" value={form.nombreResidences} onChange={change} placeholder="1" className="form-control" />
                </div>
              )}
            </>
          )}

          <div className="form-group">
            <label className="form-label">Email</label>
            <input name="email" type="email" value={form.email} onChange={change} placeholder="jean@exemple.com" className="form-control" required />
          </div>

          <div className="form-group">
            <label className="form-label">Mot de passe {mode === 'signup' ? '*' : ''}</label>
            <div style={{ position: 'relative' }}>
              <input
                name="password" type={showPwd ? 'text' : 'password'} value={form.password} onChange={change}
                placeholder="••••••••" className="form-control" style={{ paddingRight: '2.75rem' }}
                required minLength={mode === 'signup' ? 8 : 6}
              />
              <button type="button" onClick={() => setShowPwd(!showPwd)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)' }}>
                {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {mode === 'signup' && (
            <div className="form-group">
              <label className="form-label">Confirmer mot de passe *</label>
              <input name="confirmPassword" type={showPwd ? 'text' : 'password'} value={form.confirmPassword} onChange={change} placeholder="••••••••" className="form-control" required />
            </div>
          )}

          {mode === 'login' && (
            <div style={{ textAlign: 'right', marginTop: '-0.5rem', marginBottom: '1rem' }}>
              <button type="button" onClick={handleResetPassword} disabled={resetLoading}
                style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '0.8rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                {resetLoading ? 'Envoi...' : <><Send size={12} /> Mot de passe oublié ?</>}
              </button>
            </div>
          )}

          {mode === 'signup' && isGerant && (
            <div style={{ background: '#fef3d7', borderRadius: 8, padding: '0.7rem 0.85rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#7d4a00' }}>
              ℹ️ Votre compte sera activé après vérification par notre équipe.
            </div>
          )}

          <button type="submit" className="btn btn-primary btn-lg w-full" disabled={loading}>
            {loading ? 'Traitement...' : mode === 'login' ? 'Se connecter' : isGerant ? 'Créer mon compte gérant' : 'Créer mon compte'}
          </button>
        </form>
      </div>
    </div>
  );
}
